import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { logout } from '../store/slices/authSlice'
import { addNotification } from '../store/slices/uiSlice'
import { isAppForeground } from '../utils/appForeground'
import { DEFAULT_LOCALE, type AppLocale } from '../i18n/locale'
import { translate } from '../i18n/translate'

// Extend config to support suppressing global error toasts for non-critical calls
declare module 'axios' {
    interface InternalAxiosRequestConfig {
        _silent?: boolean
    }
}

const apiKey = typeof process !== 'undefined' && process.env?.REACT_APP_API_KEY

let handlingSessionExpiry = false
let lastNetworkErrorToastAt = 0
const NETWORK_ERROR_TOAST_COOLDOWN_MS = 15000

/** Replaces the backend's English duplicate-key message with a translated one naming the field. */
function localizeDuplicateValue(error: AxiosError<any>, locale: AppLocale) {
    const data = error.response?.data
    if (data?.error !== 'DuplicateValue' || typeof data.field !== 'string') {
        return
    }
    const fields = data.field
        .split(',')
        .map((column: string) => column.trim())
        .map((column: string) => {
            const key = `common.fieldNames.${column}`
            const label = translate(locale, key)
            return label === key ? column : label
        })
        .join(', ')
    data.message = translate(locale, 'common.duplicateValue', { field: fields })
}

function isPermissionDeniedMessage(message: string): boolean {
    return /you (are not|cannot)|not allowed to|not permitted|can only manage|cannot select/i.test(message)
}

function shouldTreatAsSessionExpired(
    status: number | undefined,
    message: string,
    errorCode: string,
    hadToken: boolean
): boolean {
    if (!hadToken || !status) {
        return false
    }
    if (status === 401) {
        return true
    }
    if (status !== 403 || isPermissionDeniedMessage(message)) {
        return false
    }
    const normalized = `${message} ${errorCode}`.trim().toLowerCase()
    return normalized === ''
        || normalized === 'access denied'
        || normalized === 'forbidden'
        || normalized === 'forbidden access denied'
        || normalized.includes('authentication is required')
        || normalized.includes('session expired')
        || normalized.includes('unauthorized')
}

const apiClient = axios.create({
    baseURL: '/api/v1',
    headers: {
        'Content-Type': 'application/json',
        ...(apiKey ? { 'API-KEY': apiKey, 'X-API-Key': apiKey } : {}),
    },
    timeout: 30000 // 30 seconds
})

/** Call once from the app entry point so interceptors can access the store. Avoids circular store → authSlice → auth.api → client → store. */
export function configureApiClient(store: { getState: () => { auth?: { token: string | null }; locale?: { locale: AppLocale } }; dispatch: (action: unknown) => unknown }) {
    apiClient.interceptors.request.use(
        (config) => {
            if (apiKey) {
                config.headers['API-KEY'] = apiKey
                config.headers['X-API-Key'] = apiKey
            }
            const state = store.getState()
            const token = state.auth?.token
            if (token) {
                config.headers.Authorization = `Bearer ${token}`
            }
            return config
        },
        (error) => {
            return Promise.reject(error)
        }
    )

    apiClient.interceptors.response.use(
        (response) => response,
        (error: AxiosError<any>) => {
            const silent = (error.config as InternalAxiosRequestConfig)?._silent === true
            localizeDuplicateValue(error, store.getState().locale?.locale ?? DEFAULT_LOCALE)
            const rawMessage = error.response?.data?.message || error.response?.data?.error || error.message
            const message = typeof rawMessage === 'string' ? rawMessage : String(rawMessage)
            const errorCode = typeof error.response?.data?.error === 'string'
                ? error.response.data.error
                : ''
            const isApiKeyError = /API-KEY|Authorization header must be provided/i.test(message)
            const hadToken = Boolean(store.getState().auth?.token)
            const status = error.response?.status

            if (silent) {
                return Promise.reject(error)
            }

            if (shouldTreatAsSessionExpired(status, message, errorCode, hadToken)) {
                if (!handlingSessionExpiry) {
                    handlingSessionExpiry = true
                    void Promise.resolve(store.dispatch(logout())).finally(() => {
                        handlingSessionExpiry = false
                    })
                    store.dispatch(addNotification({
                        type: 'error',
                        message: isApiKeyError
                            ? 'Gateway requires an API key. Set REACT_APP_API_KEY when starting the app (e.g. npm run web with REACT_APP_API_KEY=your-key).'
                            : 'Session expired. Please login again.'
                    }))
                }
            } else if (status === 403) {
                store.dispatch(addNotification({
                    type: 'error',
                    message: message || 'You do not have permission to perform this action.'
                }))
            } else if (error.response?.status === 404) {
                store.dispatch(addNotification({
                    type: 'error',
                    message: message || 'Resource not found.'
                }))
            } else if (error.response?.status && error.response.status >= 500) {
                store.dispatch(addNotification({
                    type: 'error',
                    message: 'Server error. Please try again later.'
                }))
            } else if (!error.response) {
                // Avoid toast storms after phone lock / tab freeze: suppress while
                // backgrounded and coalesce repeated network failures.
                if (isAppForeground()) {
                    const now = Date.now()
                    if (now - lastNetworkErrorToastAt >= NETWORK_ERROR_TOAST_COOLDOWN_MS) {
                        lastNetworkErrorToastAt = now
                        store.dispatch(addNotification({
                            type: 'error',
                            message: 'Network error. Please check your connection.'
                        }))
                    }
                }
            }

            return Promise.reject(error)
        }
    )
}

export default apiClient
